/**
 * "Pourquoi maintenant" — the AI-search shift section.
 *
 * BEFORE LINKEDIN ADS: verify every stat below against its cited source.
 * The CLAUDE.md fact-checking rule + DGCCRF (French consumer law) both
 * prohibit unsubstantiated quantitative claims in marketing. Open-methodology
 * sources (Cloudflare Radar, HTTP Archive, OpenAI official statements) are
 * defensible. Vendor benchmarks (Ahrefs, SEMrush) belong in prose with
 * attribution, never as a card stat. Update numbers below if more recent
 * official figures land before the campaign goes live.
 */

interface Stat {
  value: string;
  label: string;
  source: string;
  /** Numbered reference into the <Sources /> block below the page. */
  ref: number;
}

const STATS: Stat[] = [
  {
    value: "800 M",
    label: "Utilisateurs ChatGPT actifs par semaine — oct. 2025",
    source: "Source : Business Insider (oct. 2025) — progression 300 M (déc. 2024) → 400 M (fév. 2025) → ~800 M (oct. 2025)",
    ref: 1,
  },
  {
    value: "↗ fort",
    label: "Hausse documentée du trafic des LLM crawlers (GPTBot, ClaudeBot, PerplexityBot) en 2025",
    source: "Source : Cloudflare Radar AI Insights 2025 (croissance documentée, aucun multiple officiel)",
    ref: 2,
  },
  {
    value: "< 5 %",
    label: "Des sites français du SBF 120 publient un fichier llms.txt",
    source: "Observation propriétaire Levier IA (échantillon mars 2026) — cohérent avec crawls publics 2025 (1–10 %)",
    ref: 3,
  },
  {
    value: "8–15 k€",
    label: "Refonte web équivalente en agence parisienne (TJM 700–1 200 € HT, 4–10 jours)",
    source: "Source : seo.fr / Codeur — données marché 2025-2026",
    ref: 4,
  },
];

export function WhyNow() {
  return (
    <section id="why-now" className="py-24 px-6 bg-slate-900 border-t border-slate-800">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">
            Le tournant 2026
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Google n'est plus le seul portail d'entrée
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Votre site doit désormais convaincre <strong className="text-white">deux audiences</strong> :
            les humains qui cliquent depuis Google, et les LLMs qui résument votre contenu
            dans ChatGPT, Perplexity, Gemini. Très peu de sites français savent faire les deux.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((stat) => (
            <div
              key={stat.label}
              className="p-6 bg-slate-800/50 border border-slate-700/50 rounded-2xl hover:border-blue-500/30 transition-colors"
            >
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent mb-3">
                {stat.value}
              </div>
              <p className="text-slate-200 leading-snug font-medium text-sm">
                {stat.label}
                <sup className="ml-1 text-blue-400 font-mono text-[10px]">
                  <a href={`#ref-${stat.ref}`} className="hover:underline">[{stat.ref}]</a>
                </sup>
              </p>
              <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                {stat.source}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
